import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import chopperSwapLogo from '@/assets/chopperswap-logo-no-bg.png';

const CONSENT_KEY = 'cookie-consent';

export type ConsentLevel = 'all' | 'necessary' | null;

export function getConsentLevel(): ConsentLevel {
  return localStorage.getItem(CONSENT_KEY) as ConsentLevel;
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Small delay so it doesn't flash on page load
    const timer = setTimeout(() => {
      if (!localStorage.getItem(CONSENT_KEY)) {
        setVisible(true);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const accept = (level: 'all' | 'necessary') => {
    localStorage.setItem(CONSENT_KEY, level);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-[100] p-4",
        "lg:bottom-4 lg:left-4 lg:right-auto lg:max-w-md",
        // On mobile, sit above bottom nav
        "pb-[calc(env(safe-area-inset-bottom)+5rem)] lg:pb-4"
      )}
    >
      <div className="bg-card/95 backdrop-blur-xl border border-border rounded-2xl shadow-2xl p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center gap-2 shrink-0 mt-0.5">
            <Cookie className="h-5 w-5 text-primary" />
            <img src={chopperSwapLogo} alt="ChopperSwap" className="h-6 w-auto dark:brightness-0 dark:invert" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">We value your privacy</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              We use cookies to improve your experience. You can accept all cookies or only necessary ones.{' '}
              <Link to="/cookies" className="underline underline-offset-2 hover:text-foreground transition-colors">
                Cookie Policy
              </Link>
            </p>
          </div>
          <button
            onClick={() => accept('necessary')}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1 text-xs h-9"
            onClick={() => accept('necessary')}
          >
            Necessary Only
          </Button>
          <Button
            size="sm"
            className="flex-1 text-xs h-9"
            onClick={() => accept('all')}
          >
            Accept All
          </Button>
        </div>
      </div>
    </div>
  );
}
